import { Button, Css, ModalProps, RightPaneLayout, ScrollableContent, Switch, useModal } from "@homebound/beam";
import { History } from "history";
import { useState } from "react";
import { useHistory } from "react-router";
import { SearchBox } from "src/components";
import { NamedFragment } from "src/fragments/commonFragments.generated";
import { BidPackageStatus } from "src/generated/graphqlTypes";
import { PageBidPackageDetailFragment } from "src/routes/bids/BidPackageDetailPage.generated";
import { PlanBasedBidsTableV2 } from "src/routes/bids/plan-based-bid-package/components/PlanBasedBidsTableV2";
import { TableActions } from "src/routes/layout/TableActions";
import { createEnterPlanBasedBidUrl } from "src/RouteUrls";
import { noop, pushInNextLoop } from "src/utils";
import { SelectTradePartnerModal } from "../plan-based-bid-package/components/SelectTradePartnerModal";
import { AddTradePartnerModal } from "./AddTradePartnerModal";
import { PlanBasedBidsTable } from "./PlanBasedBidsTable";
import { UnitBasedBidsTable } from "./UnitBasedBidsTable";
import { getBidPackageTradesWithEstimate } from "./utils";

type BidsTabProps = { bidPackage: PageBidPackageDetailFragment };

export function BidsTab(props: BidsTabProps) {
  const { bidPackage } = props;
  const { openModal } = useModal();
  const history = useHistory();
  const [searchFilter, setSearchFilter] = useState<string | undefined>("");
  const [showPlanUsage, setShowPlanUsage] = useState(!bidPackage.isUnitBased);

  // Get all trades without contracts or with draft contracts
  const trades = getBidPackageTradesWithEstimate(bidPackage);
  const tradePartners = trades.map((trade) => trade.tradePartner);
  const requestsWithEditableBids = trades.filter((trade) => {
    // If the bid package is awarded, we don't allow edits
    if (bidPackage.status.code === BidPackageStatus.Awarded) {
      return false;
    }
    const bidContractRevision = trade.bidContractRevision;
    return !bidContractRevision || bidContractRevision.canEdit.allowed;
  });

  return (
    <>
      <TableActions>
        <SearchBox onSearch={setSearchFilter} />
        {bidPackage.isUnitBased && (
          <Switch label="Show Plan Usage" selected={showPlanUsage} onChange={setShowPlanUsage} />
        )}
        {bidPackage.isPlanBasedV2 && (
          <div css={Css.df.gap1.$}>
            <Button
              label="Enter Bid"
              onClick={() => goToEnterPlanBasedBidPage(bidPackage, tradePartners, openModal, history)}
              variant="textSecondary"
              icon="pencil"
            />
            <Button label="Upload Bid" onClick={noop} variant="textSecondary" icon="upload" />
          </div>
        )}
      </TableActions>
      <div css={Css.h100.$}>
        {!showPlanUsage ? (
          // Since the plan based table has tabs the scrollable content is wrapped inside that component
          <ScrollableContent virtualized>
            <RightPaneLayout>
              <div css={Css.h100.$}>
                <UnitBasedBidsTable
                  bidPackage={bidPackage}
                  searchFilter={searchFilter}
                  requestsWithEditableBids={requestsWithEditableBids}
                />
              </div>
            </RightPaneLayout>
          </ScrollableContent>
        ) : bidPackage.isPlanBasedV2 ? (
          <PlanBasedBidsTableV2 bidPackage={bidPackage} searchFilter={searchFilter} />
        ) : (
          <PlanBasedBidsTable
            bidPackage={bidPackage}
            searchFilter={searchFilter}
            requestsWithEditableBids={requestsWithEditableBids}
          />
        )}
      </div>
    </>
  );
}

export function getOptionsKey(opt: { __typename?: "ReadyPlanOption" | undefined; id: string }[]) {
  return opt
    .sortByKey("id")
    .map((o) => o.id)
    .join(",");
}

/**
 * Before going to the enter plan based bid page, we need to check if there is only one trade partner,
 * if there are no trade partners, we need to open the add trade partner modal,
 * if there are multiple trade partners, we need to open the select trade partner modal.
 */
function goToEnterPlanBasedBidPage(
  bidPackage: PageBidPackageDetailFragment,
  tradePartners: NamedFragment[],
  openModal: (props: ModalProps) => void,
  history: History,
) {
  const goToEnterPlanBasedBid = (tradePartnerId: string) => {
    pushInNextLoop(history, createEnterPlanBasedBidUrl(bidPackage.id, tradePartnerId));
  };

  if (tradePartners.length === 1) {
    goToEnterPlanBasedBid(tradePartners[0].id);
  } else if (tradePartners.isEmpty) {
    openModal({ content: <AddTradePartnerModal bidPackage={bidPackage} /> });
  } else {
    openModal({ content: <SelectTradePartnerModal tradePartners={tradePartners} onSelect={goToEnterPlanBasedBid} /> });
  }
}
